// src/services/files/FileUploadService.js - HTTP File Operations Utility
import { API_ENDPOINTS } from '../../config/api';
import axios from 'axios';

/**
 * FileUploadService - Utility for HTTP file operations
 * 
 * This service handles file uploads/deletions via HTTP endpoints.
 * It's separate from WebSocket services but integrates with them.
 * 
 * Design Philosophy:
 * - Pure HTTP operations (no WebSocket)
 * - Proper error handling and validation
 * - Integration with existing auth system via axios interceptors
 * - Returns structured responses for easy integration
 */
class FileUploadService {
  
  /**
   * Upload a photo to a job note
   * 
   * @param {string} noteId - Note ID to attach photo to
   * @param {string} eventId - Event ID (for validation)
   * @param {File} file - File object from input
   * @param {string} caption - Optional photo caption
   * @returns {Promise<object>} Upload result
   */
  static async uploadPhotoToNote(noteId, eventId, file, caption = '') {
    try {
      // Validate inputs
      if (!noteId || !eventId || !file) {
        throw new Error('Note ID, Event ID, and file are required');
      }

      // Validate file type (client-side check)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only images are allowed.');
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        throw new Error('File too large. Maximum size is 10MB.');
      }

      console.log('📁 FileUploadService: Uploading photo to note:', noteId);

      // Prepare form data
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('note_id', noteId);
      formData.append('event_id', eventId);
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      // Make HTTP request using axios (automatic auth via interceptors)
      const response = await axios.post(`${API_ENDPOINTS.files}/upload/job-photo`, formData);

      // Axios automatically parses JSON and handles HTTP errors
      const result = response.data;

      if (result.status !== 'success') {
        throw new Error(result.message || 'Upload failed');
      }

      console.log('✅ FileUploadService: Photo uploaded successfully:', result.data?.photo?.id);

      return {
        success: true,
        data: result.data,
        message: result.message || 'Photo uploaded successfully'
      };

    } catch (error) {
      console.error('❌ FileUploadService: Photo upload failed:', error.message);
      
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null
      };
    }
  }

  /**
   * Delete a photo from a job note
   * 
   * @param {string} photoId - Photo ID to delete
   * @returns {Promise<object>} Deletion result
   */
  static async deletePhoto(photoId) {
    try {
      // Validate input
      if (!photoId) {
        throw new Error('Photo ID is required');
      }

      console.log('🗑️ FileUploadService: Deleting photo:', photoId);

      // Make HTTP request using axios (automatic auth via interceptors)
      const response = await axios.delete(`${API_ENDPOINTS.files}/delete/job-photo/${photoId}`);

      // Axios automatically parses JSON and handles HTTP errors
      const result = response.data;

      if (result.status !== 'success') {
        throw new Error(result.message || 'Deletion failed');
      }

      console.log('✅ FileUploadService: Photo deleted successfully:', photoId);

      return {
        success: true,
        data: result.data,
        message: result.message || 'Photo deleted successfully'
      };

    } catch (error) {
      console.error('❌ FileUploadService: Photo deletion failed:', error.message);
      
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: null
      };
    }
  }

  /**
   * Upload multiple photos to a job note
   * 
   * @param {string} noteId - Note ID to attach photos to
   * @param {string} eventId - Event ID (for validation)
   * @param {FileList|Array} files - Files to upload
   * @param {Array<string>} captions - Optional captions (same order as files)
   * @returns {Promise<object>} Upload results
   */
  static async uploadMultiplePhotos(noteId, eventId, files, captions = []) {
    try {
      console.log('📁 FileUploadService: Uploading multiple photos:', files.length);

      const results = [];
      const errors = [];

      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const caption = captions[i] || '';

        const result = await this.uploadPhotoToNote(noteId, eventId, file, caption);
        
        if (result.success) {
          results.push(result.data);
        } else {
          errors.push({
            filename: file.name,
            error: result.error
          });
        }
      }

      const successCount = results.length;
      const errorCount = errors.length;

      console.log(`📁 FileUploadService: Batch upload complete - ${successCount} success, ${errorCount} errors`);

      return {
        success: errorCount === 0,
        data: {
          uploaded_photos: results,
          successful_uploads: successCount,
          failed_uploads: errorCount,
          errors: errors
        },
        message: errorCount === 0 
          ? `${successCount} photos uploaded successfully`
          : `${successCount} photos uploaded, ${errorCount} failed`
      };

    } catch (error) {
      console.error('❌ FileUploadService: Batch upload failed:', error.message);
      
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  /**
   * Get photo URL for display
   * 
   * @param {string} filename - Photo filename from database
   * @returns {string} Full URL to photo
   */
  static getPhotoUrl(filename) {
    if (!filename) {
      return null;
    }

    // Return URL to your existing file serving endpoint
    return `${API_ENDPOINTS.files}/${filename}`;
  }

  /**
   * Validate file before upload (client-side validation)
   * 
   * @param {File} file - File to validate
   * @returns {object} Validation result
   */
  static validateFile(file) {
    const errors = [];

    // Check file exists
    if (!file) {
      errors.push('No file provided');
      return { valid: false, errors };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      errors.push('Invalid file type. Only images are allowed.');
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      errors.push('File too large. Maximum size is 10MB.');
    }

    // Check filename
    if (file.name.length > 255) {
      errors.push('Filename too long (max 255 characters)');
    }

    return {
      valid: errors.length === 0,
      errors,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type,
        sizeFormatted: this.formatFileSize(file.size)
      }
    };
  }

  /**
   * Format file size for display
   * 
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted size string
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export default FileUploadService;